import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Paperclip, X, FileText, Pin, Trash2, RotateCcw } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { MarkdownView } from '@/components/app/MarkdownView';
import {
  useRunAgent, useAiRuns, useAiSpend, agentLabel,
  useAiResults, usePinAiResult, useDeleteAiResult,
  type AgentKind, type AgentResult, type AiSavedResult,
} from '@/lib/aiAgents';

/** Returns true if the string looks like a UUID (v4 or any standard UUID format). */
function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

const KINDS: AgentKind[] = ['compliance', 'onboarding', 'policy', 'comp', 'offer', 'qa'];

const SAMPLE_QUESTIONS: Record<AgentKind, string> = {
  compliance: 'What is the FY2026 雇用保険 employee rate for general business? Cite the source.',
  onboarding: 'A new hire starts in Japan next Monday. What documents must I collect within 5 days?',
  policy:     'Draft the section of our 就業規則 about remote work (在宅勤務). Cite the statutory floor.',
  comp:       'Senior backend engineer (IC5) in Tokyo. What is market + how does our offer compare?',
  offer:      'Draft an offer letter for Tanaka-san: Senior Engineer, ¥9M base, start 2026-07-01.',
  qa:         'How does the JP-DE social security totalization agreement work for a 3-year posting?',
};

const ALLOWED_EXT = ['.pdf', '.docx', '.txt', '.md', '.png', '.jpg', '.jpeg'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function AgentsPage() {
  const { t, i18n } = useTranslation();
  const [agent, setAgent] = useState<AgentKind>('compliance');
  const [question, setQuestion] = useState<string>(SAMPLE_QUESTIONS.compliance);
  const [lastResult, setLastResult] = useState<AgentResult | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const run = useRunAgent();
  const recent = useAiRuns();
  const spend = useAiSpend();
  const savedResults = useAiResults();

  /** Reopen a saved result into the answer pane (no re-run). */
  const reopen = (r: AiSavedResult) => {
    setAgent(r.agent_kind);
    setQuestion(r.input_text);
    setLastResult({
      answer: r.output_text, model: r.model ?? '', inputTokens: 0, outputTokens: 0,
      costCents: 0, citedRecords: r.cited_record_ids ?? [], resultId: r.id,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onPickFile = (f: File | undefined | null) => {
    setFileError(null);
    if (!f) return;
    const ext = '.' + (f.name.split('.').pop() ?? '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setFileError(`Unsupported file type. Allowed: ${ALLOWED_EXT.join(', ')}.`);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError(`File is too large (max ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB).`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    onPickFile(e.dataTransfer.files?.[0]);
  };

  async function execute() {
    if (!question.trim()) return;
    try {
      const r = await run.mutateAsync({
        agentKind: agent,
        question: question.trim(),
        file: file ?? undefined,
      });
      setLastResult(r);
    } catch {
      // run.error will surface below
    }
  }

  return (
    <AppShell>
      <div
        className="px-8 py-8 max-w-6xl"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('agents.title', { defaultValue: 'AI Agents' })}</h1>
            <p className="mt-1 text-sm text-ink-500">
              {t('agents.subtitle', { defaultValue: 'Cited answers from authoritative sources. Attach a contract, policy doc, or scan and the assistant reads it for you.' })}
            </p>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white px-4 py-2 text-end">
            <p className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">{t('agents.thisMonth', { defaultValue: 'This month' })}</p>
            <p className="num text-base font-bold text-accent-700">${((spend.data?.spentCents ?? 0) / 100).toFixed(2)}</p>
          </div>
        </div>

        <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <aside className="rounded-2xl border border-ink-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50">
              <h2 className="text-sm font-semibold text-ink-700">{t('agents.agentsHeading', { defaultValue: 'Agents' })}</h2>
            </div>
            <ul className="divide-y divide-ink-100">
              {KINDS.map((k) => {
                const meta = agentLabel(k, i18n.language);
                return (
                  <li key={k}>
                    <button
                      onClick={() => { setAgent(k); setQuestion(SAMPLE_QUESTIONS[k]); }}
                      className={`block w-full text-start px-4 py-3 transition ${agent === k ? 'bg-accent-50' : 'hover:bg-ink-50'}`}
                    >
                      <p className={`text-sm font-semibold ${agent === k ? 'text-accent-700' : 'text-ink-900'}`}>{meta.name}</p>
                      <p className="text-[11px] text-ink-500 mt-0.5">{meta.hint}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <main className="lg:col-span-2 space-y-4">
            <div className={`rounded-2xl border bg-white p-5 transition ${dragOver ? 'border-accent-500 ring-2 ring-accent-500/20' : 'border-ink-200'}`}>
              <label className="block">
                <span className="block text-xs font-medium text-ink-700 mb-1.5">{t('agents.yourQuestion', { defaultValue: 'Your question' })}</span>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={3}
                  className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-3 focus:ring-accent-500/10"
                />
              </label>

              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_EXT.join(',')}
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />

              {file ? (
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-ink-200 bg-ink-50/50 px-3 py-2">
                  <FileText className="h-4 w-4 text-accent-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-ink-900 truncate">{file.name}</p>
                    <p className="text-[10px] text-ink-500">{formatBytes(file.size)} · server reads the document and adds it as context</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-ink-200/60 transition"
                    aria-label={t('agents.removeFile', { defaultValue: 'Remove file' })}
                  >
                    <X className="h-3.5 w-3.5 text-ink-500" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-600 hover:text-accent-700 transition"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {t('agents.attachDocument', { defaultValue: 'Attach document' })} <span className="text-ink-400">(PDF · DOCX · TXT · PNG · JPG · max 10 MB)</span>
                </button>
              )}

              {fileError && (
                <p className="mt-2 text-xs text-warn-600">{fileError}</p>
              )}

              <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
                <button
                  onClick={execute}
                  disabled={!question.trim() || run.isPending}
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition"
                >
                  {run.isPending ? (file ? t('agents.readingThinking', { defaultValue: 'Reading + thinking…' }) : t('agents.thinking', { defaultValue: 'Thinking…' })) : t('agents.ask', { defaultValue: '✦ Ask' })}
                </button>
              </div>
              {run.isError && (
                <p className="mt-3 text-xs text-warn-600">
                  {(run.error as { response?: { data?: { message?: string } } })?.response?.data?.message
                    || (run.error as Error)?.message
                    || t('agents.runFailed', { defaultValue: 'Run failed — check OPENROUTER_API_KEY on the server.' })}
                </p>
              )}
            </div>

            {lastResult && (
              <div className="rounded-2xl border border-ink-200 bg-white p-5">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-ink-900">{t('agents.answer', { defaultValue: 'Answer' })}</h3>
                  <div className="flex items-center gap-3 text-[10px] text-ink-500">
                    <span className="num">${(lastResult.costCents / 100).toFixed(4)}</span>
                    <span className="num">{lastResult.inputTokens + lastResult.outputTokens} tok</span>
                  </div>
                </div>
                <div className="mt-3 text-ink-800">
                  {lastResult.answer
                    ? <MarkdownView>{lastResult.answer}</MarkdownView>
                    : <p className="text-sm text-muted-foreground italic">(no answer text — agent may have ended without final text)</p>}
                </div>
                {lastResult.citedRecords.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-ink-100">
                    <p className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold mb-1.5">{t('agents.citedSources', { defaultValue: 'Cited sources' })}</p>
                    <ul className="space-y-1">
                      {lastResult.citedRecords.map((c) => (
                        <li key={c} className="text-[11px] text-ink-700 font-mono">
                          {isUuid(c) ? (
                            <Link
                              to={`/app/people/${c}`}
                              className="text-accent-700 underline decoration-dotted hover:decoration-solid"
                            >
                              [{c}]
                            </Link>
                          ) : (
                            <span>[{c}]</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <SavedResultsPanel onReopen={reopen} />

            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-ink-900 mb-2">{t('agents.recentRuns', { defaultValue: 'Recent runs (this workspace)' })}</h3>
              <ul className="divide-y divide-ink-100 -mx-3">
                {recent.data?.length === 0 && <li className="px-3 py-3 text-xs text-ink-500">{t('agents.noRuns', { defaultValue: 'No runs yet.' })}</li>}
                {(recent.data ?? []).slice(0, 10).map((r) => {
                  const meta = agentLabel(r.agent_kind, i18n.language);
                  // Try to find a matching saved result to enable reopen
                  const matchedSaved = (savedResults.data ?? []).find(
                    (s) => s.agent_kind === r.agent_kind &&
                      Math.abs(new Date(s.created_at).getTime() - new Date(r.created_at).getTime()) < 5000
                  );
                  return (
                    <li key={r.id} className="px-3 py-2 flex items-center justify-between gap-2">
                      {matchedSaved ? (
                        <button
                          type="button"
                          onClick={() => reopen(matchedSaved)}
                          className="flex-1 min-w-0 text-start group"
                          title={t('agents.reopenRun', { defaultValue: 'Reopen this result' })}
                        >
                          <span className="text-xs font-medium text-ink-700 group-hover:text-accent-700 transition">{meta.name}</span>
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-ink-700 flex-1 min-w-0">{meta.name}</span>
                      )}
                      <span className="text-[10px] text-ink-500 num">${((r.cost_cents ?? 0) / 100).toFixed(4)}</span>
                      <span className="text-[10px] text-ink-400">{new Date(r.created_at).toLocaleString()}</span>
                      {matchedSaved && (
                        <button
                          type="button"
                          onClick={() => reopen(matchedSaved)}
                          className="text-ink-400 hover:text-accent-700 transition"
                          title={t('agents.reopenRun', { defaultValue: 'Reopen this result' })}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </main>
        </section>
      </div>
    </AppShell>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Saved AI results — list past Q&A, reopen one into the answer pane (no
 * re-run / re-spend), pin the useful ones, delete the rest. Closes the
 * "I can see the answer but can't reuse it" gap.
 */
function SavedResultsPanel({ onReopen }: { onReopen: (r: AiSavedResult) => void }) {
  const { t, i18n } = useTranslation();
  const results = useAiResults();
  const pin = usePinAiResult();
  const del = useDeleteAiResult();
  const rows = results.data ?? [];
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-ink-900 mb-2">{t('agents.savedResults', { defaultValue: 'Saved results · 保存済み' })}</h3>
      {rows.length === 0 && <p className="text-xs text-ink-500">{t('agents.noSavedResults', { defaultValue: 'No saved results yet. Every answer you run is saved here to reopen later.' })}</p>}
      <ul className="divide-y divide-ink-100 -mx-3">
        {rows.slice(0, 20).map((r) => {
          const meta = agentLabel(r.agent_kind, i18n.language);
          return (
            <li key={r.id} className="px-3 py-2">
              <div className="flex items-start gap-2">
                <button onClick={() => onReopen(r)} className="flex-1 min-w-0 text-start group" title={t('agents.reopen', { defaultValue: 'Reopen' })}>
                  <p className="text-xs font-medium text-ink-800 truncate group-hover:text-accent-700">
                    {r.pinned && '📌 '}{r.title}
                  </p>
                  <p className="text-[10px] text-ink-400">{meta.name} · {new Date(r.created_at).toLocaleDateString()}</p>
                </button>
                <button onClick={() => onReopen(r)} className="text-ink-400 hover:text-accent-700" title={t('agents.reopen', { defaultValue: 'Reopen' })}><RotateCcw className="h-3.5 w-3.5" /></button>
                <button onClick={() => pin.mutate({ id: r.id, pinned: !r.pinned })} className={`hover:text-accent-700 ${r.pinned ? 'text-accent-600' : 'text-ink-400'}`} title={r.pinned ? t('agents.unpin', { defaultValue: 'Unpin' }) : t('agents.pin', { defaultValue: 'Pin' })}><Pin className="h-3.5 w-3.5" /></button>
                <button onClick={() => del.mutate(r.id)} className="text-ink-400 hover:text-rose-600" title={t('agents.delete', { defaultValue: 'Delete' })}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
